import { Component, EventEmitter, inject, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Params, Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { ToastItem } from 'projects/shared/lib/components/shared/toast/item/toast-item.model';
import { EToastItemType } from 'projects/shared/lib/enum/toast/toast-entry-type.enum';
import { IAuthService, IAuthServiceToken } from 'projects/shared/lib/services/infrastructure/authentication/auth-service.interface';
import { LoggerService } from 'projects/shared/lib/services/infrastructure/logger/logger.service';
import { ServiceLocator } from 'projects/shared/lib/services/infrastructure/service-locator.service';
import { CustomerDto, EError } from 'projects/shared/lib/services/INTEGRA_Shop_Client';
import { firstValueFrom } from 'rxjs';
import { ENVIRONMENT } from 'projects/shared/lib/environment/environment';
import { INavigationEnvironment } from 'projects/shared/lib/interfaces/environment/navigation/navigation-environment.interface';
import { IUserService, IUserServiceToken } from 'projects/shared/lib/services/data/user/user-service.interface';
import { ErrorCodeService } from 'projects/shared/lib/services/infrastructure/error-code.service';
import { CustomerNamesToStringPipe, CustomerNamesToStringService } from 'projects/shared/lib/pipes/customer/customernames-to-string/customernames-to-string.pipe';
import { IConfigurationServiceToken } from 'projects/shared/lib/services/infrastructure/configuration/configuration-service.interface';
import { IToastService, IToastServiceToken } from 'shared/services/ui/toast/toast-service.interface';

@Component({
  selector: 'app-user-switch-customer-dialog',
  imports: [
    CommonModule,
    FormsModule,
    CustomerNamesToStringPipe
  ],
  templateUrl: './user-switch-customer-dialog.component.html',
  styleUrls: ['./user-switch-customer-dialog.component.scss']
})
export class UserSwitchCustomerDialogComponent implements OnInit {

  @Output() modalCancel: EventEmitter<void> = new EventEmitter();

  private logger: LoggerService;
  public customerUserList: CustomerDto[] | undefined;
  public customerUserSelected: CustomerDto | undefined;
  private readonly authService: IAuthService;
  private targetPage: INavigationEnvironment;
  protected readonly userService: IUserService;
  private readonly toastService: IToastService = inject(IToastServiceToken);

  public constructor() {
    this.logger = ServiceLocator.injector.get(LoggerService);
    this.authService = inject(IAuthServiceToken);
    this.userService = inject(IUserServiceToken);

    let isMultiCustomerUser = this.authService.isMultiCustomerUser();
    if (isMultiCustomerUser == false) {
      throw new Error("Missing permission for switching customer in switch user dialog.");
    }
    this.targetPage = ENVIRONMENT.authorizedLandingPage;
    ServiceLocator.injector.get(IConfigurationServiceToken).authorizedLandingPageGet().subscribe((targetPage) => {
      this.targetPage.routerlink = targetPage.routerlink as string;
      let queryParams = new URLSearchParams(targetPage.queryParams);
      let queryParamsObject: Params = {};
      queryParams.forEach((value, key) => {
        queryParamsObject[key] = value;
      });
      this.targetPage.queryParams = queryParamsObject;
    });
  }

  public ngOnInit(): void {
    this.customerUserListLoadAsync();
  }

  protected async customerUserListLoadAsync() {
    try {
      let result = await firstValueFrom(this.userService.userCustomerListGet());
      this.customerUserList = result.customerList;
    } catch (error) {
      this.logger.error(error, this.constructor.name);
    }
  }

  public close() {
    this.modalCancel.emit();
  }

  public async switchUserAsync(customerUser: CustomerDto | undefined) {
    if (customerUser == undefined) {
      return;
    }

    try {
      let accessJWT = await firstValueFrom(this.userService.userSwitchCustomer(customerUser.customerNumber));
      if (accessJWT.length <= 0) {
        let message = $localize`:@@UserSwitchCustomerDialogFailedNoAccessJWTReceived:No new credentials received`;
        this.switchUserFailedToastShow(message);
        return;
      }
      let accessJWTStored = this.authService.accessJWTSet(accessJWT);
      if (accessJWTStored != undefined) {
        let targetPage = this.targetPage;
        ServiceLocator.injector.get(Router).navigate([targetPage.routerlink], { queryParams: targetPage.queryParams });
        let message = $localize`:@@UserSwitchCustomerDialogSuccessMessage:You're now shopping for:`;
        message += `<br><br>`;
        message += CustomerNamesToStringService.transform(customerUser);
        this.toastService.toastItemAdd(new ToastItem({
          type: EToastItemType.Success,
          title: $localize`:@@UserSwitchCustomerDialogSuccessTitle:Customer switched`,
          message: message,
        }));

        setTimeout(() => {
          firstValueFrom(this.userService.userFavoritesInitialize());
        });

        this.close();
      } else {
        let message = $localize`:@@UserSwitchCustomerDialogFailedAccessJwtNotReplaced:Permissions could not be updated.`;
        this.switchUserFailedToastShow(message);
      }

    } catch (error) {
      let message = $localize`:@@UserSwitchCustomerDialogFailed:Switch customer failed.`;
      this.switchUserFailedToastShow(message);
      this.logger.error(error, this.constructor.name);
    }
  }

  private switchUserFailedToastShow(message?: string | undefined, type: EToastItemType | undefined = EToastItemType.Danger) {
    this.toastService.toastItemAdd(new ToastItem({
      type: type,
      title: $localize`:@@UserSwitchCustomerDialogFailedTitle:Switch customer failed`,
      message: message,
      code: ErrorCodeService.formatErrorCode(EError.F200011)
    }));
  }

}
